import { useState } from 'react';
import { useApp } from '../context/AppContext';
import { rentalService, dateChangeService } from '../api/services';
import StatCard from '../components/StatCard';
import { buildContractContext } from '../utils/contractContext';
import { printContract } from '../utils/renderContract';
import { calculatePartialRefund, buildRefundRecord } from '../utils/bookingPolicy';

const PAY_LABELS = {
  stripe: '💳 Card (Stripe)',
  viva:   '🔵 Viva Wallet',
  paypal: '🅿️ PayPal',
  cash:   '💵 Cash at pickup',
  bank:   '🏦 Bank Transfer',
  online: '🌐 Pre-paid',
};

function isImg(s) {
  return typeof s === 'string' && (s.startsWith('data:image') || s.startsWith('http') || s.startsWith('/') || s.startsWith('blob:'));
}

export default function IncomingPage() {
  const {
    rentals, setRentals, setRentalHistory,
    vehicles, insurance, showToast, addInvoice, nextInvoiceNum,
    invoices, setInvoices,
    companySettings, resolveAgreementTemplate,
    dateChangeRequests, setDateChangeRequests, refunds = [], setRefunds,
    currentUserId, goto,
  } = useApp();

  // Helper: print the contract for a rental (used by View / Print buttons)
  function openContract(r, autoPrint) {
    const v = vehicles.find(vv => vv.id === r.vid);
    const tmpl = r.agreementSaved?.body
      ? { name: r.agreementSaved.templateName, body: r.agreementSaved.body, clauses: r.agreementSaved.clauses }
      : (resolveAgreementTemplate ? resolveAgreementTemplate(r.vid, r.type === 'lease' ? 'lease' : 'rental') : null);
    const ctx = r.agreementSaved?.context || buildContractContext({
      rental: r, vehicle: v, company: companySettings,
      signatureDataUrl: r.signData || r.presignData,
      signedAt: r.signedAt || r.presignedAt,
    });
    printContract(tmpl, ctx, { title: `Contract ${r.id || ''}`, autoPrint });
  }

  const [expandedId, setExpandedId] = useState(null);
  const [expandedConfId, setExpandedConfId] = useState(null);

  const pending = rentals.filter(r =>
    r.source === 'online' && (r.confirmed === false || r.bankTransferPending)
  );

  const confirmed = rentals.filter(r =>
    r.source === 'online' && r.confirmed === true
  );

  function confirmBooking(r) {
    const isBankTransfer = r.bankTransferPending || r.payMethod === 'bank';

    setRentals(prev => prev.map(x => x.id === r.id
      ? { ...x, isConfirmed: true, bankTransferPending: false, bankTransferVerified: isBankTransfer }
      : x
    ));
    // Persist to API
    const backendId = r._backendId || r.id;
    rentalService.update(backendId, { ...r, is_confirmed: true, bank_transfer_pending: false, bank_transfer_verified: isBankTransfer }).catch(() => {});

    // Update existing invoice to paid, or create one if none exists
    if (isBankTransfer) {
      const existing = (invoices || []).find(inv => inv.contract === r.id);
      if (existing) {
        // Just mark the existing invoice as paid
        setInvoices(prev => prev.map(inv => inv.contract === r.id ? { ...inv, status: 'paid' } : inv));
        showToast('✓ Bank transfer verified — pro forma marked as paid');
      } else {
        // No invoice yet — create one (fallback for manual/old bookings)
        const v = vehicles.find(x => x.id === r.vid);
        const days = r.calSpan || 1;
        const today = new Date();
        const fmtDate = d => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
        const isoDate = d => d.toISOString().split('T')[0];
        const dueDate = new Date(today.getTime() + 7 * 86400000);
        const invNum = nextInvoiceNum();
        addInvoice({
          id: invNum, num: invNum,
          customer: r.customer, email: r.email || '', addr: '',
          vehicle: v?.name || r.vid, contract: r.id, vid: r.vid,
          method: 'bank', methodLabel: 'Bank Transfer',
          issue: fmtDate(today), due: fmtDate(dueDate),
          issueDate: isoDate(today), dueDate: isoDate(dueDate),
          lines: [{ desc: `Vehicle rental — ${v?.name || r.vid} (${days} days)`, qty: days, unit: r.rate || 0, total: r.agreedPrice || 0 }],
          subtotal: r.agreedPrice || 0, vatRate: 0, vatAmt: 0,
          discount: 0, discType: 'eur', total: r.agreedPrice || 0,
          notes: `Bank transfer confirmed. Booking ref: ${r.bookingRef || r.id}`,
          status: 'paid', source: 'bank-confirm',
        });
        showToast('✓ Bank transfer verified — booking confirmed & pro forma created');
      }
    } else {
      showToast('✓ Booking confirmed — added to calendar');
    }
  }

  function declineBooking(r) {
    if (!window.confirm(`Decline booking for ${r.customer}? This cannot be undone.`)) return;
    setRentals(prev => prev.filter(x => x.id !== r.id));
    setRentalHistory(prev => [{
      ...r, status: 'cancelled',
      total: r.agreedPrice, condRet: null, condRetNote: 'Booking declined.',
    }, ...prev]);
    const backendId = r._backendId || r.id;
    rentalService.update(backendId, { ...r, status: 'cancelled' }).catch(() => {});
    showToast('Booking declined');
  }

  function DetailPanel({ r, v }) {
    const isBankTransfer = r.bankTransferPending || r.payMethod === 'bank';
    const hasPhotos = isImg(r.passportPhoto) || isImg(r.licencePhoto);
    const hasSig = r.presigned && r.presignData;

    return (
      <div style={{
        borderTop: '1px solid var(--bd)',
        background: 'var(--sur2)',
        padding: '14px 16px',
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gap: 16,
      }}>

        {/* Col 1 — Customer details */}
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>Customer Details</div>
          <Row label="Name"        value={r.customer} bold />
          {r.phone       && <Row label="Phone"       value={r.phone} />}
          {r.email       && <Row label="Email"       value={r.email} />}
          {r.passport    && <Row label="Passport/ID" value={r.passport} mono />}
          {r.licence     && <Row label="Licence"     value={r.licence} mono />}
          {r.nationality && <Row label="Nationality" value={r.nationality} />}

          {/* Signature */}
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.06em', marginTop: 12, marginBottom: 6 }}>Signature</div>
          {hasSig ? (
            <>
              <div style={{ fontSize: 10, color: 'var(--gr)', fontWeight: 600, marginBottom: 4 }}>✅ Pre-signed online — {r.presignedAt || ''}</div>
              <img src={r.presignData} alt="Signature"
                style={{ width: '100%', maxWidth: 200, background: '#fff', border: '1px solid var(--bd)', borderRadius: 6, maxHeight: 60, objectFit: 'contain' }} />
            </>
          ) : (
            <div style={{ fontSize: 11, color: 'var(--tx3)' }}>Not signed yet — signs at check-in</div>
          )}
          {/* View / Print contract — always available (unsigned contracts render with a "Not yet signed" stamp so they can be printed blank) */}
          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            <button className="btn btn-ghost btn-sm" style={{ fontSize: 11 }} onClick={() => openContract(r, false)}>📋 View contract</button>
            <button className="btn btn-ghost btn-sm" style={{ fontSize: 11 }} onClick={() => openContract(r, true)}>
              🖨️ Print{!hasSig && <span style={{ color: 'var(--am)' }}> · unsigned</span>}
            </button>
          </div>
        </div>

        {/* Col 2 — Booking & Financial */}
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>Booking Info</div>
          {r.bookingRef  && <Row label="Ref"       value={r.bookingRef} mono blue />}
          <Row label="Vehicle"   value={(v?.icon || '') + ' ' + (v?.name || r.vid)} />
          <Row label="Pickup"    value={r.startLabel || r.start} mono />
          <Row label="Return"    value={r.endLabel   || r.end}   mono />
          <Row label="Duration"  value={`${r.calSpan || '?'} days`} />
          {r.pickupLocName && <Row label="📍 Pickup loc" value={r.pickupLocName} />}
          {r.returnLocName && r.returnLocName !== r.pickupLocName && <Row label="📍 Return loc" value={r.returnLocName} />}
          {r.t1 && <Row label="Pickup time" value={r.t1} mono />}
          {r.t2 && <Row label="Return time" value={r.t2} mono />}

          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.06em', marginTop: 12, marginBottom: 6 }}>Financial</div>
          <Row label="Total"   value={`€${r.agreedPrice}`} bold green />
          <Row label="Payment" value={PAY_LABELS[r.payMethod] || r.payMethod} />
          {['stripe','online','viva','paypal'].includes(r.payMethod) && (
            <div style={{ background: 'var(--bl-l)', border: '1px solid var(--bl-b)', borderRadius: 6, padding: '7px 10px', marginTop: 6, fontSize: 11 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--tx3)' }}>Pre-paid (online)</span>
                <span style={{ color: 'var(--gr)', fontWeight: 600 }}>€{(r.agreedPrice || 0).toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 2 }}>
                <span style={{ color: 'var(--tx3)' }}>Due at desk</span>
                <span style={{ color: 'var(--gr)', fontWeight: 600 }}>€0.00</span>
              </div>
              {r.deposit > 0 && (
                <div style={{ marginTop: 4, fontSize: 10, color: 'var(--tx3)' }}>
                  🔒 Security deposit €{r.deposit} collected separately at pickup
                </div>
              )}
            </div>
          )}
          {isBankTransfer && (
            <div style={{ background: 'var(--am-l)', border: '1px solid var(--am-b)', borderRadius: 6, padding: '7px 10px', marginTop: 6, fontSize: 11, color: 'var(--am)' }}>
              🏦 Verify €{r.agreedPrice} received before confirming
            </div>
          )}

          {/* Extras / Insurance */}
          {(r.insurance || r.extras?.length > 0) && (
            <>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.06em', marginTop: 12, marginBottom: 6 }}>Extras</div>
              {r.insurance && (() => {
                // Look up full plan details — check vehicle custom plans first, then global
                const veh = vehicles.find(x => x.id === r.vid);
                const insPool = (veh?.insuranceMode === 'custom' && veh.customInsurance?.length > 0)
                  ? veh.customInsurance
                  : insurance;
                const plan = r.insuranceId ? insPool.find(p => p.id === r.insuranceId) : null;
                return (
                  <div style={{ background: 'var(--bl-l)', border: '1px solid var(--bl-b)', borderRadius: 6, padding: '8px 10px', marginBottom: 6 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                      <span style={{ fontSize: 16 }}>{plan?.icon || '🛡️'}</span>
                      <span style={{ fontSize: 11, fontWeight: 700, color: plan?.color || 'var(--bl)' }}>{r.insurance}</span>
                      {plan?.recommended && (
                        <span style={{ fontSize: 8, background: plan.color || 'var(--bl)', color: '#fff', padding: '1px 5px', borderRadius: 8, fontWeight: 700 }}>Recommended</span>
                      )}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 10px', fontSize: 10, color: 'var(--tx3)' }}>
                      {plan?.price > 0
                        ? <div>💰 <strong style={{ color: 'var(--tx)' }}>€{plan.price}/day</strong></div>
                        : <div>💰 <strong style={{ color: 'var(--gr)' }}>Included</strong></div>
                      }
                      {plan?.excess && <div>⚠️ Excess: <strong style={{ color: 'var(--tx)' }}>{plan.excess}</strong></div>}
                      {(plan?.deposit > 0 || r.deposit > 0) && (
                        <div>🔒 Deposit: <strong style={{ color: 'var(--am)' }}>€{plan?.deposit || r.deposit}</strong></div>
                      )}
                      {plan?.desc && <div style={{ gridColumn: '1/-1', marginTop: 2, fontStyle: 'italic' }}>{plan.desc}</div>}
                    </div>
                  </div>
                );
              })()}
              {r.extras?.length > 0 && <Row label="Extras" value={r.extras.join(', ')} />}
            </>
          )}

          {r.notes && !r.notes.toLowerCase().includes('online booking') && (
            <div style={{ fontSize: 11, color: 'var(--tx2)', background: 'var(--sur)', border: '1px solid var(--bd)', borderRadius: 6, padding: '7px 10px', marginTop: 8 }}>
              💬 {r.notes}
            </div>
          )}
        </div>

        {/* Col 3 — Documents */}
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>Documents</div>
          {hasPhotos ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {isImg(r.passportPhoto) && (
                <div>
                  <div style={{ fontSize: 10, color: 'var(--tx3)', marginBottom: 3 }}>Passport / ID</div>
                  <img src={r.passportPhoto} alt="Passport/ID"
                    style={{ width: '100%', borderRadius: 6, border: '1px solid var(--bd)', maxHeight: 110, objectFit: 'cover' }} />
                </div>
              )}
              {isImg(r.licencePhoto) && (
                <div>
                  <div style={{ fontSize: 10, color: 'var(--tx3)', marginBottom: 3 }}>Driving Licence</div>
                  <img src={r.licencePhoto} alt="Driving Licence"
                    style={{ width: '100%', borderRadius: 6, border: '1px solid var(--bd)', maxHeight: 110, objectFit: 'cover' }} />
                </div>
              )}
            </div>
          ) : (
            <div style={{ fontSize: 11, color: 'var(--tx3)', padding: '10px 0' }}>No documents uploaded yet</div>
          )}
        </div>

      </div>
    );
  }

  function Row({ label, value, mono, bold, blue, green }) {
    return (
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, padding: '3px 0', borderBottom: '1px solid var(--bd)' }}>
        <span style={{ color: 'var(--tx3)', flexShrink: 0, marginRight: 8 }}>{label}</span>
        <span style={{
          fontWeight: bold ? 700 : 500,
          textAlign: 'right',
          fontFamily: mono ? "'DM Mono',monospace" : undefined,
          color: blue ? 'var(--bl)' : green ? 'var(--gr)' : undefined,
        }}>{value || '—'}</span>
      </div>
    );
  }

  return (
    <div>
      {/* Live banner — pending bookings + date-change requests + refunds */}
      {(() => {
        const pendingDC      = (dateChangeRequests || []).filter(d => d.status === 'pending').length;
        const pendingRefunds = refunds.filter(r => r.status === 'pending').length;
        const totalToAction = pending.length + pendingDC + pendingRefunds;
        if (totalToAction === 0) return null;
        const bits = [];
        if (pending.length > 0)   bits.push(`${pending.length} booking${pending.length > 1 ? 's' : ''} to confirm`);
        if (pendingDC > 0)        bits.push(`${pendingDC} date change request${pendingDC > 1 ? 's' : ''}`);
        if (pendingRefunds > 0)   bits.push(`${pendingRefunds} refund${pendingRefunds > 1 ? 's' : ''} to process`);
        return (
          <div style={{
            background: 'linear-gradient(135deg,var(--gr),#0f5c36)',
            color: '#fff', borderRadius: 'var(--r)', padding: '9px 14px',
            display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14,
          }}>
            <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#7fff7f', flexShrink: 0 }} />
            <div style={{ fontSize: 12 }}>
              <strong>{bits.join(' · ')}</strong>
              {pending[0]?.bankTransferPending && ' — verify bank transfer before confirming'}
            </div>
          </div>
        );
      })()}

      {/* Stats */}
      <div className="sg sg-4" style={{ marginBottom: 14 }}>
        <StatCard label="Pending"        value={pending.length} desc="Awaiting confirmation" variant="am" />
        <StatCard label="Bank Transfers" value={pending.filter(r => r.bankTransferPending).length} desc="Verify payment first" variant="bl" />
        <StatCard label="Confirmed"      value={confirmed.length} desc="Online bookings" variant="gr" />
        <StatCard label="Online Revenue" value={`€${confirmed.reduce((s,r) => s + (r.agreedPrice||0), 0).toLocaleString()}`} desc="Confirmed this month" variant="ac" />
      </div>

      {/* ── Date change requests panel ──────────────────────────────── */}
      <DateChangeRequestsPanel
        requests={(dateChangeRequests || []).filter(d => d.status === 'pending')}
        rentals={rentals} vehicles={vehicles}
        setRentals={setRentals}
        setDateChangeRequests={setDateChangeRequests}
        setRefunds={setRefunds}
        companySettings={companySettings}
        currentUserId={currentUserId}
        showToast={showToast}
      />

      {/* ── Refunds-pending notice (links to the Refunds page) ──────── */}
      {(() => {
        const pendingR = refunds.filter(r => r.status === 'pending');
        if (pendingR.length === 0) return null;
        const total = pendingR.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
        return (
          <div style={{
            background: 'var(--am-l)', border: '1.5px solid var(--am-b)',
            borderRadius: 'var(--rl)', padding: '12px 16px', marginBottom: 16,
            display: 'flex', alignItems: 'center', gap: 12,
          }}>
            <span style={{ fontSize: 22 }}>💸</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--am)' }}>
                {pendingR.length} refund{pendingR.length === 1 ? '' : 's'} pending — €{total.toFixed(2)} to process
              </div>
              <div style={{ fontSize: 10, color: 'var(--tx3)' }}>
                Process each in your payment provider (Stripe / Viva / bank) and mark them done.
              </div>
            </div>
            <button className="btn btn-primary btn-sm" onClick={() => goto && goto('refunds')}>
              Open Refunds →
            </button>
          </div>
        );
      })()}

      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>
        Pending — awaiting your confirmation
      </div>

      {pending.length === 0 && (
        <div style={{ padding: 30, textAlign: 'center', color: 'var(--tx3)', fontSize: 12, background: 'var(--sur)', border: '1px solid var(--bd)', borderRadius: 'var(--rl)', marginBottom: 16 }}>
          No pending bookings — all clear ✓
        </div>
      )}

      {pending.map(r => {
        const v = vehicles.find(x => x.id === r.vid);
        const isBankTransfer = r.bankTransferPending || r.payMethod === 'bank';
        const isOpen = expandedId === r.id;
        return (
          <div key={r.id} style={{
            background: 'var(--sur)', border: `1px solid ${isBankTransfer ? 'var(--am-b)' : 'var(--bd)'}`,
            borderRadius: 'var(--rl)', marginBottom: 10, overflow: 'hidden', boxShadow: 'var(--sh)',
          }}>
            {/* Top bar */}
            <div style={{
              padding: '7px 14px', background: isBankTransfer ? 'var(--am-l)' : 'var(--sur2)',
              borderBottom: '1px solid var(--bd)',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 10,
            }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 5, color: isBankTransfer ? 'var(--am)' : 'var(--tx3)', fontWeight: isBankTransfer ? 600 : 400 }}>
                {isBankTransfer ? '🏦 Bank transfer — verify payment before confirming' : '🌐 Online booking — fleetrent.app'}
              </span>
              <span style={{ color: 'var(--tx3)', fontFamily: "'DM Mono',monospace" }}>{r.createdAt}</span>
            </div>

            {/* Main row — click to expand */}
            <div
              style={{ display: 'grid', gridTemplateColumns: '1.6fr 1.6fr 1fr 1fr auto auto', gap: 14, padding: 14, alignItems: 'center', cursor: 'pointer' }}
              onClick={() => setExpandedId(isOpen ? null : r.id)}
            >
              {/* Vehicle */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 20 }}>{v?.icon || '🚗'}</span>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 500 }}>{v?.name || r.vid}</div>
                  <div style={{ fontSize: 10, color: 'var(--tx3)' }}>{v?.type?.toUpperCase()} · {v?.plate}</div>
                </div>
              </div>
              {/* Customer */}
              <div>
                <div style={{ fontSize: 12, fontWeight: 500 }}>{r.customer}</div>
                <div style={{ fontSize: 10, color: 'var(--tx3)' }}>{r.phone}</div>
                <div style={{ fontSize: 10, color: 'var(--tx3)' }}>{r.email}</div>
              </div>
              {/* Dates */}
              <div style={{ fontSize: 11, fontFamily: "'DM Mono',monospace", color: 'var(--tx2)' }}>
                {r.startLabel}{r.t1 ? ` · ${r.t1}` : ''} →<br />
                {r.endLabel}{r.t2 ? ` · ${r.t2}` : ''}<br />
                <span style={{ color: 'var(--tx3)' }}>{r.calSpan || '?'} days</span>
                {r.pickupLocName && (
                  <div style={{ fontSize: 10, color: 'var(--tx3)', fontFamily: 'inherit', marginTop: 2 }}>
                    📍 {r.pickupLocName}{r.returnLocName && r.returnLocName !== r.pickupLocName ? ` → ${r.returnLocName}` : ''}
                  </div>
                )}
              </div>
              {/* Price */}
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--br)', fontFamily: "'DM Mono',monospace" }}>€{r.agreedPrice}</div>
                <div style={{ fontSize: 10, color: 'var(--tx3)' }}>{PAY_LABELS[r.payMethod] || r.payMethod}</div>
                {r.bookingRef && <div style={{ fontSize: 10, color: 'var(--bl)', fontFamily: "'DM Mono',monospace" }}>{r.bookingRef}</div>}
              </div>
              {/* Actions — stop click propagation */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }} onClick={e => e.stopPropagation()}>
                <button className="btn btn-sm" style={{ background: 'var(--gr)', color: '#fff', border: 'none', borderRadius: 6, padding: '5px 10px', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}
                  onClick={() => confirmBooking(r)}>
                  ✓ Confirm
                </button>
                <button className="btn btn-sm" style={{ background: 'var(--ac-l)', color: 'var(--ac)', border: '1px solid var(--ac-b)', borderRadius: 6, padding: '5px 10px', cursor: 'pointer', fontSize: 11 }}
                  onClick={() => declineBooking(r)}>
                  ✕ Decline
                </button>
              </div>
              {/* Chevron */}
              <div style={{ fontSize: 12, color: 'var(--tx3)', transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }}>▾</div>
            </div>

            {/* Bank transfer warning */}
            {isBankTransfer && !isOpen && (
              <div style={{ margin: '0 14px 12px', background: 'var(--am-l)', border: '1px solid var(--am-b)', borderRadius: 6, padding: '8px 12px', fontSize: 11, color: 'var(--am)' }}>
                ⚠️ Do not confirm until you have verified that <strong>€{r.agreedPrice}</strong> has been received in your bank account. The customer was sent your IBAN by email.
              </div>
            )}

            {/* Expanded detail panel */}
            {isOpen && <DetailPanel r={r} v={v} />}
          </div>
        );
      })}

      {/* Confirmed list */}
      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, marginTop: 16 }}>Confirmed online bookings</div>
      <div className="tbl">
        <div className="tbl-hd" style={{ gridTemplateColumns: '1fr 1.4fr 1fr 1fr .9fr .7fr .3fr' }}>
          <div className="th">Ref</div>
          <div className="th">Customer</div>
          <div className="th">Vehicle</div>
          <div className="th">Dates</div>
          <div className="th">Value</div>
          <div className="th">Payment</div>
          <div className="th"></div>
        </div>
        {confirmed.length === 0 && (
          <div style={{ padding: '14px', textAlign: 'center', fontSize: 11, color: 'var(--tx3)' }}>No confirmed bookings yet</div>
        )}
        {confirmed.map(r => {
          const v = vehicles.find(x => x.id === r.vid);
          const isOpen = expandedConfId === r.id;
          return (
            <div key={r.id} style={{ borderBottom: '1px solid var(--bd)' }}>
              {/* Row */}
              <div className="tr" style={{ gridTemplateColumns: '1fr 1.4fr 1fr 1fr .9fr .7fr .3fr', cursor: 'pointer' }}
                onClick={() => setExpandedConfId(isOpen ? null : r.id)}>
                <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 10, color: 'var(--bl)' }}>{r.bookingRef || r.id}</div>
                <div style={{ fontSize: 11, fontWeight: 500 }}>{r.customer}</div>
                <div style={{ fontSize: 11 }}>{v?.icon} {v?.name?.split('–')[0].trim() || r.vid}</div>
                <div className="dv">{r.startLabel} → {r.endLabel}</div>
                <div className="pv">€{r.agreedPrice}</div>
                <div style={{ fontSize: 10 }}><span className="sp s-ac">Confirmed</span></div>
                <div style={{ fontSize: 12, color: 'var(--tx3)', transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform .2s', textAlign: 'center' }}>▾</div>
              </div>
              {/* Expanded */}
              {isOpen && <DetailPanel r={r} v={v} />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Date Change Requests panel — operator-side accept/refuse for self-service
// date changes submitted by customers via the public Manage Booking portal.
// ────────────────────────────────────────────────────────────────────────────
function DateChangeRequestsPanel({ requests, rentals, vehicles, setRentals, setDateChangeRequests, setRefunds, companySettings, currentUserId, showToast }) {
  const [refusing, setRefusing] = useState(null);     // request id being refused
  const [refuseReason, setRefuseReason] = useState('');

  if (!requests || requests.length === 0) return null;

  function accept(req) {
    const rental = rentals.find(r => r.id === req.rentalId);
    if (!rental) { showToast('⚠ Rental not found — may have been cancelled.'); return; }
    const veh = vehicles.find(v => v.id === rental.vid);

    const fmtLabel = (iso) => iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
    const newStartIso = req.newStart + 'T' + (rental.t1 || '09:00');
    const newEndIso   = req.newEnd   + 'T' + (rental.t2 || '09:00');

    // Scale original price proportionally — preserves VAT, insurance, extras baked into agreedPrice
    const oldDays  = req.oldDays || Math.max(1, Math.round((new Date(rental.end) - new Date(rental.start)) / 86400000));
    const newPrice = Math.round((rental.agreedPrice || 0) * (req.newDays / oldDays) * 100) / 100;
    const diff     = Math.round((newPrice - (rental.agreedPrice || 0)) * 100) / 100;
    const paidOnline = ['stripe', 'viva', 'paypal', 'online'].includes(rental.payMethod);

    setRentals(prev => prev.map(r => r.id === rental.id ? {
      ...r,
      start: newStartIso, end: newEndIso,
      startLabel: fmtLabel(newStartIso), endLabel: fmtLabel(newEndIso),
      calSpan: req.newDays, days: req.newDays,
      agreedPrice: newPrice,
      // Flag for check-in — operator collects/refunds the difference
      ...(diff > 0  && paidOnline ? { differenceOwed: diff } : {}),
      ...(diff > 0 && !paidOnline ? { } : {}),    // cash → no flag needed, customer pays new total
      dateChangeCount: (r.dateChangeCount || 0) + 1,
    } : r));

    // If customer is owed money AND paid online → create refund record
    if (diff < 0 && paidOnline) {
      const preview = calculatePartialRefund(Math.abs(diff), rental.payMethod, companySettings);
      const refundRecord = buildRefundRecord({
        rental: { ...rental, vehicleName: veh?.name || rental.vid },
        amount: preview.finalRefund,
        originalAmount: Math.abs(diff),
        scenario: 'partial',
        reason: `Date change shortening · ${req.oldDays}→${req.newDays} days · request ${req.id}`,
        payMethod: rental.payMethod,
        originalPaymentRef: rental.bookingRef || rental.id,
      });
      setRefunds(prev => [refundRecord, ...prev]);
    }

    // Mark request accepted
    setDateChangeRequests(prev => prev.map(d => d.id === req.id ? {
      ...d, status: 'accepted',
      decidedAt: new Date().toISOString(),
      decidedBy: currentUserId,
      finalPrice: newPrice, finalDiff: diff,
    } : d));
    // Persist to API
    const backendDcrId = req._backendId || req.id;
    dateChangeService.accept(backendDcrId, { new_start_at: req.newStart, new_end_at: req.newEnd }).catch(() => {});

    showToast(diff > 0
      ? `✓ Dates updated — ${paidOnline ? `€${diff.toFixed(2)} owed at pickup` : `customer pays new total at pickup`}`
      : diff < 0
      ? `✓ Dates updated — €${Math.abs(diff).toFixed(2)} refund queued`
      : '✓ Dates updated');
  }

  function refuse(reqId) {
    setDateChangeRequests(prev => prev.map(d => d.id === reqId ? {
      ...d, status: 'refused',
      decidedAt: new Date().toISOString(),
      decidedBy: currentUserId,
      decisionNote: refuseReason || '(no reason given)',
    } : d));
    const req = requests.find(r => r.id === reqId);
    const backendId = req?._backendId || reqId;
    dateChangeService.refuse(backendId).catch(() => {});
    setRefusing(null); setRefuseReason('');
    showToast('✗ Request refused — customer will be emailed');
  }

  return (
    <div style={{
      background: 'var(--bl-l)', border: '1.5px solid var(--bl-b)',
      borderRadius: 'var(--rl)', padding: '14px 16px', marginBottom: 16,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <span style={{ fontSize: 16 }}>📅</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--bl)' }}>
            {requests.length} date change request{requests.length === 1 ? '' : 's'} waiting
          </div>
          <div style={{ fontSize: 10, color: 'var(--tx3)' }}>Customer self-service requests — review and accept/refuse</div>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {requests.map(req => {
          const rental = rentals.find(r => r.id === req.rentalId);
          const veh = rental ? vehicles.find(v => v.id === rental.vid) : null;
          const isRefusing = refusing === req.id;
          const diffColor = req.priceDiff > 0 ? 'var(--am)' : req.priceDiff < 0 ? 'var(--gr)' : 'var(--tx3)';
          const paidOnline = rental && ['stripe', 'viva', 'paypal', 'online'].includes(rental.payMethod);
          return (
            <div key={req.id} style={{
              background: '#fff', border: '1px solid var(--bd)', borderRadius: 'var(--r)',
              padding: '12px 14px',
            }}>
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>
                    {req.customer} <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--tx3)', fontFamily: "'DM Mono',monospace" }}>· {req.bookingRef || req.rentalId}</span>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--tx3)', marginTop: 2 }}>
                    {veh?.icon || '🚗'} {veh?.name || req.vid}{req.email ? ` · ${req.email}` : ''}
                  </div>
                </div>
                <div style={{ fontSize: 10, color: 'var(--tx3)' }}>
                  {new Date(req.createdAt).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
              {/* Dates comparison */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 12, alignItems: 'center', padding: '10px 12px', background: 'var(--sur2)', border: '1px solid var(--bd)', borderRadius: 6, fontSize: 12 }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 9, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.05em' }}>From</div>
                  <div style={{ fontWeight: 600 }}>{req.oldStartLabel} → {req.oldEndLabel}</div>
                  <div style={{ fontSize: 10, color: 'var(--tx3)' }}>{req.oldDays} days · €{(req.oldPrice || 0).toFixed(2)}</div>
                </div>
                <div style={{ fontSize: 18, color: 'var(--tx3)' }}>→</div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 9, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.05em' }}>To</div>
                  <div style={{ fontWeight: 600 }}>
                    {new Date(req.newStart).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} → {new Date(req.newEnd).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--tx3)' }}>{req.newDays} days · €{(req.newPriceEst || 0).toFixed(2)}</div>
                </div>
              </div>
              {/* Price diff banner */}
              {req.priceDiff !== 0 && (
                <div style={{
                  marginTop: 8, padding: '8px 12px', fontSize: 11,
                  background: req.priceDiff > 0 ? 'var(--am-l)' : 'var(--gr-l)',
                  border: `1px solid ${req.priceDiff > 0 ? 'var(--am-b)' : 'var(--gr-b)'}`,
                  borderRadius: 6, color: diffColor, fontWeight: 600,
                }}>
                  {req.priceDiff > 0
                    ? <>💰 +€{req.priceDiff.toFixed(2)} owed · {paidOnline ? 'flag rental "DIFFERENCE DUE" — collect at pickup' : 'customer pays new total at pickup'}</>
                    : <>↩ −€{Math.abs(req.priceDiff).toFixed(2)} refund · {paidOnline ? 'will queue partial refund' : 'customer pays the lower total at pickup'}</>}
                </div>
              )}
              {/* Reason from customer */}
              {req.reason && (
                <div style={{ marginTop: 8, fontSize: 11, color: 'var(--tx2)', fontStyle: 'italic' }}>
                  💬 "{req.reason}"
                </div>
              )}
              {/* Refuse inline */}
              {isRefusing ? (
                <div style={{ marginTop: 10, padding: '8px 10px', background: 'var(--ac-l)', border: '1px solid var(--ac-b)', borderRadius: 6 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ac)', marginBottom: 6 }}>
                    Refuse this request? Customer will be emailed.
                  </div>
                  <input type="text" className="fi" placeholder="Reason (e.g. vehicle not available those dates, peak season)"
                    value={refuseReason} onChange={e => setRefuseReason(e.target.value)}
                    style={{ marginBottom: 8 }} />
                  <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => { setRefusing(null); setRefuseReason(''); }}>Cancel</button>
                    <button className="btn btn-primary btn-sm" style={{ background: 'var(--ac)' }} onClick={() => refuse(req.id)}>Confirm Refuse</button>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 6, marginTop: 10, justifyContent: 'flex-end' }}>
                  <button className="btn btn-ghost btn-sm" style={{ color: 'var(--ac)' }} onClick={() => setRefusing(req.id)}>
                    ❌ Refuse
                  </button>
                  <button className="btn btn-primary btn-sm" onClick={() => accept(req)}>
                    ✅ Accept
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
